// SPDX-License-Identifier: MIT
// SQL Panel Redux Slice for Atlas Viewer
// Manages saved queries, panel state, and query execution tracking

import type { QueryExecutionState, TableSchema, SqlPanelState } from './types';

// ============================================================================
// Initial State
// ============================================================================

const INITIAL_EXECUTION_STATE: QueryExecutionState = {
  isExecuting: false,
  result: null,
  error: null,
  lastExecutedAt: null,
};

const INITIAL_STATE: SqlPanelState = {
  currentSql: '',
  execution: INITIAL_EXECUTION_STATE,
  schemas: [],
  isPanelOpen: false,
  isSyncingTables: false,
};

// ============================================================================
// Action Types
// ============================================================================

export const SQL_PANEL_ACTIONS = {
  TOGGLE_PANEL: '@sqlPanel/TOGGLE_PANEL',
  SET_PANEL_OPEN: '@sqlPanel/SET_PANEL_OPEN',
  SET_CURRENT_SQL: '@sqlPanel/SET_CURRENT_SQL',
  SET_EXECUTION_STATE: '@sqlPanel/SET_EXECUTION_STATE',
  SET_EXECUTION_RESULT: '@sqlPanel/SET_EXECUTION_RESULT',
  SET_EXECUTION_ERROR: '@sqlPanel/SET_EXECUTION_ERROR',
  CLEAR_EXECUTION: '@sqlPanel/CLEAR_EXECUTION',
  SET_SCHEMAS: '@sqlPanel/SET_SCHEMAS',
  ADD_SCHEMA: '@sqlPanel/ADD_SCHEMA',
  REMOVE_SCHEMA: '@sqlPanel/REMOVE_SCHEMA',
  SET_SYNCING_TABLES: '@sqlPanel/SET_SYNCING_TABLES',
} as const;

// ============================================================================
// Action Interfaces
// ============================================================================

interface TogglePanelAction {
  type: typeof SQL_PANEL_ACTIONS.TOGGLE_PANEL;
}

interface SetPanelOpenAction {
  type: typeof SQL_PANEL_ACTIONS.SET_PANEL_OPEN;
  payload: boolean;
}

interface SetCurrentSqlAction {
  type: typeof SQL_PANEL_ACTIONS.SET_CURRENT_SQL;
  payload: string;
}

interface SetExecutionStateAction {
  type: typeof SQL_PANEL_ACTIONS.SET_EXECUTION_STATE;
  payload: Partial<QueryExecutionState>;
}

interface SetExecutionResultAction {
  type: typeof SQL_PANEL_ACTIONS.SET_EXECUTION_RESULT;
  payload: QueryExecutionState['result'];
}

interface SetExecutionErrorAction {
  type: typeof SQL_PANEL_ACTIONS.SET_EXECUTION_ERROR;
  payload: string;
}

interface ClearExecutionAction {
  type: typeof SQL_PANEL_ACTIONS.CLEAR_EXECUTION;
}

interface SetSchemasAction {
  type: typeof SQL_PANEL_ACTIONS.SET_SCHEMAS;
  payload: TableSchema[];
}

interface AddSchemaAction {
  type: typeof SQL_PANEL_ACTIONS.ADD_SCHEMA;
  payload: TableSchema;
}

interface RemoveSchemaAction {
  type: typeof SQL_PANEL_ACTIONS.REMOVE_SCHEMA;
  payload: string; // tableName
}

interface SetSyncingTablesAction {
  type: typeof SQL_PANEL_ACTIONS.SET_SYNCING_TABLES;
  payload: boolean;
}

export type SqlPanelAction =
  | TogglePanelAction
  | SetPanelOpenAction
  | SetCurrentSqlAction
  | SetExecutionStateAction
  | SetExecutionResultAction
  | SetExecutionErrorAction
  | ClearExecutionAction
  | SetSchemasAction
  | AddSchemaAction
  | RemoveSchemaAction
  | SetSyncingTablesAction;

// ============================================================================
// Action Creators
// ============================================================================

export const toggleSqlPanel = (): TogglePanelAction => ({
  type: SQL_PANEL_ACTIONS.TOGGLE_PANEL,
});

export const setSqlPanelOpen = (isOpen: boolean): SetPanelOpenAction => ({
  type: SQL_PANEL_ACTIONS.SET_PANEL_OPEN,
  payload: isOpen,
});

export const setCurrentSql = (sql: string): SetCurrentSqlAction => ({
  type: SQL_PANEL_ACTIONS.SET_CURRENT_SQL,
  payload: sql,
});

export const setExecutionState = (
  state: Partial<QueryExecutionState>
): SetExecutionStateAction => ({
  type: SQL_PANEL_ACTIONS.SET_EXECUTION_STATE,
  payload: state,
});

export const setExecutionResult = (
  result: QueryExecutionState['result']
): SetExecutionResultAction => ({
  type: SQL_PANEL_ACTIONS.SET_EXECUTION_RESULT,
  payload: result,
});

export const setExecutionError = (error: string): SetExecutionErrorAction => ({
  type: SQL_PANEL_ACTIONS.SET_EXECUTION_ERROR,
  payload: error,
});

export const clearExecution = (): ClearExecutionAction => ({
  type: SQL_PANEL_ACTIONS.CLEAR_EXECUTION,
});

export const setSchemas = (schemas: TableSchema[]): SetSchemasAction => ({
  type: SQL_PANEL_ACTIONS.SET_SCHEMAS,
  payload: schemas,
});

export const addSchema = (schema: TableSchema): AddSchemaAction => ({
  type: SQL_PANEL_ACTIONS.ADD_SCHEMA,
  payload: schema,
});

export const removeSchema = (tableName: string): RemoveSchemaAction => ({
  type: SQL_PANEL_ACTIONS.REMOVE_SCHEMA,
  payload: tableName,
});

export const setSyncingTables = (isSyncing: boolean): SetSyncingTablesAction => ({
  type: SQL_PANEL_ACTIONS.SET_SYNCING_TABLES,
  payload: isSyncing,
});

// ============================================================================
// Reducer
// ============================================================================

export function sqlPanelReducer(
  state: SqlPanelState = INITIAL_STATE,
  action: SqlPanelAction
): SqlPanelState {
  switch (action.type) {
    case SQL_PANEL_ACTIONS.TOGGLE_PANEL:
      return { ...state, isPanelOpen: !state.isPanelOpen };

    case SQL_PANEL_ACTIONS.SET_PANEL_OPEN:
      return { ...state, isPanelOpen: action.payload };

    case SQL_PANEL_ACTIONS.SET_CURRENT_SQL:
      return { ...state, currentSql: action.payload };

    case SQL_PANEL_ACTIONS.SET_EXECUTION_STATE:
      return {
        ...state,
        execution: { ...state.execution, ...action.payload },
      };

    case SQL_PANEL_ACTIONS.SET_EXECUTION_RESULT:
      return {
        ...state,
        execution: {
          ...state.execution,
          isExecuting: false,
          result: action.payload,
          error: null,
          lastExecutedAt: Date.now(),
        },
      };

    case SQL_PANEL_ACTIONS.SET_EXECUTION_ERROR:
      return {
        ...state,
        execution: {
          ...state.execution,
          isExecuting: false,
          result: null,
          error: action.payload,
          lastExecutedAt: Date.now(),
        },
      };

    case SQL_PANEL_ACTIONS.CLEAR_EXECUTION:
      return {
        ...state,
        execution: INITIAL_EXECUTION_STATE,
      };

    case SQL_PANEL_ACTIONS.SET_SCHEMAS:
      return { ...state, schemas: action.payload };

    case SQL_PANEL_ACTIONS.ADD_SCHEMA: {
      // Replace if same table name exists
      const exists = state.schemas.some(s => s.tableName === action.payload.tableName);
      if (exists) {
        return {
          ...state,
          schemas: state.schemas.map(s =>
            s.tableName === action.payload.tableName ? action.payload : s
          ),
        };
      }
      return { ...state, schemas: [...state.schemas, action.payload] };
    }

    case SQL_PANEL_ACTIONS.REMOVE_SCHEMA:
      return {
        ...state,
        schemas: state.schemas.filter(s => s.tableName !== action.payload),
      };

    case SQL_PANEL_ACTIONS.SET_SYNCING_TABLES:
      return { ...state, isSyncingTables: action.payload };

    default:
      return state;
  }
}

// ============================================================================
// Selectors
// ============================================================================

export interface RootStateWithSqlPanel {
  sqlPanel: SqlPanelState;
}

export const selectSqlPanelState = (state: RootStateWithSqlPanel): SqlPanelState => state.sqlPanel;

export const selectIsSqlPanelOpen = (state: RootStateWithSqlPanel): boolean =>
  state.sqlPanel.isPanelOpen;

export const selectCurrentSql = (state: RootStateWithSqlPanel): string => state.sqlPanel.currentSql;

export const selectExecutionState = (state: RootStateWithSqlPanel): QueryExecutionState =>
  state.sqlPanel.execution;

export const selectIsExecuting = (state: RootStateWithSqlPanel): boolean =>
  state.sqlPanel.execution.isExecuting;

export const selectExecutionResult = (
  state: RootStateWithSqlPanel
): QueryExecutionState['result'] => state.sqlPanel.execution.result;

export const selectExecutionError = (state: RootStateWithSqlPanel): string | null =>
  state.sqlPanel.execution.error;

export const selectSchemas = (state: RootStateWithSqlPanel): TableSchema[] =>
  state.sqlPanel.schemas;

export const selectHasSecureSchemas = (state: RootStateWithSqlPanel): boolean =>
  state.sqlPanel.schemas.some(s => s.isSecure);

export const selectIsSyncingTables = (state: RootStateWithSqlPanel): boolean =>
  state.sqlPanel.isSyncingTables;
